// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleDescriptorVO } from '../model/vo/validation-context.vo';
import { ProjectHelper } from '../helper/project.helper';
import { SelectionModel } from '../components/article/model/selection.model';
import { SelectionModelService } from '../components/article/service/selection-model.service';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleMoveManager } from '../components/article/manager/article-move.manager';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { DetachArticleUsageService } from './detach-article-usage.service';
import { ProjectModel } from '../model/project.model';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { SequenceOperation } from '@shared/undo/operation/sequence-operation';
import { ChangeLtlArticleService } from '@article/ltl/change-ltl-article.service';
import { IsProjectType } from '@project/model/is-project-type';
import { UserUtils } from '@user/service/user-utils';
import { UserModel } from '@user/model/user.model';

@Injectable({providedIn: 'root'})
export class RemoveProjectArticleService {

  constructor(
    private readonly projectModel: ProjectModel,
    private readonly userModel: UserModel,
    private readonly projectHelper: ProjectHelper,
    private readonly selectionModel: SelectionModel,
    private readonly selectionModelService: SelectionModelService,
    private readonly undoRedoHelper: UndoRedoHelper,
    private readonly articleContainerUtils: ArticleContainerUtils,
    private readonly articleMoveManager: ArticleMoveManager,
    private readonly applicationRefreshService: ApplicationRefreshService,
    private readonly detachArticleUsageService: DetachArticleUsageService,
    private readonly containerService: ContainerService,
    private readonly changeLtlArticleService: ChangeLtlArticleService,
  ) {
  }

    public async removeArticles(articleUsageIds: string[], pushUndoOperations: boolean = true, closeGap: boolean = false,
      gapLeftArticleDescriptor: ArticleDescriptorVO = null, gapRightArticleDescriptor: ArticleDescriptorVO = null): Promise<void> {
      if (ArrayUtils.isEmpty(articleUsageIds)) {
        return;
      }
      await this.projectHelper.deleteArticles(articleUsageIds, (articleUsageIdsToRemove: string[]): Promise<SequenceOperation> => {
        return this.proceedRemoveArticles(articleUsageIdsToRemove, pushUndoOperations, closeGap,
          gapLeftArticleDescriptor, gapRightArticleDescriptor);
      });
    }

    private async proceedRemoveArticles(articleUsageIds: string[], pushUndoOperations: boolean = true,
      closeGap: boolean = false, gapLeftArticleDescriptor: ArticleDescriptorVO = null,
                                        gapRightArticleDescriptor: ArticleDescriptorVO = null): Promise<SequenceOperation> {
      let undoOperation: SequenceOperation = new SequenceOperation([]);
      if (ArrayUtils.isEmpty(articleUsageIds)) {
        return null;
      }
      let detachedUsageIds: string[] = [];
      const usageIdsToRefresh: string[] = [];
      for (const usageId of articleUsageIds) {
        detachedUsageIds = detachedUsageIds.concat(await this.detachArticleUsageService.internalDetachArticleUsageFromProjectById(usageId, true, null, usageIdsToRefresh));
      }
      if (detachedUsageIds.length > 0) {
        this.selectionModelService.removeAllSelections();
        const articleContainers = this.containerService.findArticleContainersInGlobalByGuids(detachedUsageIds);
        const articleUsages = articleContainers.map((articleContainer) => articleContainer.articleUsage);
        if (pushUndoOperations) {
          undoOperation = new SequenceOperation([ ...undoOperation.operations, this.undoRedoHelper.createUndoDetachOperationByUsageIds(detachedUsageIds) ]);
        }

        if (closeGap && gapLeftArticleDescriptor && gapRightArticleDescriptor) {
          const articleContainers: ArticleContainer[] = this.articleContainerUtils.getArticleContainersByArticleDescriptors([
            gapLeftArticleDescriptor,
            gapRightArticleDescriptor,
          ]);
          if (ArrayUtils.isNotEmpty(articleContainers)) {
            await this.articleMoveManager.removeFreeSpaceBetweenArticlesHandler(articleContainers);
          }
        }
        await this.undoRedoHelper.doWithSequence(async () => {
          await this.changeLtlArticleService.articlesRemoved(articleUsages);
          const additionalOperations = this.undoRedoHelper.popAllFromRunningSequence();
          if (pushUndoOperations) {
            undoOperation = new SequenceOperation([ ...undoOperation.operations, ...additionalOperations ]);
          }
        });

        // Do this last, because when done earlier parts of the needed objects are already disposed.
        this.applicationRefreshService.notifyProjectChangeInternal(usageIdsToRefresh);
      }
      return undoOperation;
    }

    public async removeSelectedArticles(): Promise<void> {
      if (IsProjectType.ltl(this.projectModel.currentProjectType) && !UserUtils.isWagoUser(this.userModel.user)) {
        return;
      }
      await this.removeArticles(this.selectionModel.getArticleUsageIds());
    }

    public async detachArticleUsagesFromProjectByArticleDescriptor(articleDescriptor: ArticleDescriptorVO,
      closeGap: boolean = false, gapLeftArticleDescriptor: ArticleDescriptorVO = null,
      gapRightArticleDescriptor: ArticleDescriptorVO = null): Promise<void> {
      const articleUsageIds: string[] = ArticleUsageUtils.getArticleUsageIdsByArticleDescriptor(articleDescriptor);

      const allArticleUsageIds: string[] = [];

      for (const usageId of articleUsageIds) {
        const compositeUsages = this.projectModel.getCompositePartArticleUsagesById(usageId);
        if (compositeUsages.length > 0) {
          for (const usage of compositeUsages) {
            allArticleUsageIds.push(usage.id);
          }
        } else {
          allArticleUsageIds.push(usageId);
        }
      }

      await this.removeArticles(allArticleUsageIds, true, closeGap, gapLeftArticleDescriptor, gapRightArticleDescriptor);
    }
}
